import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Send, Star } from "lucide-react";
import Navbar from "@/components/campus/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Chat() {
  const { id } = useParams();
  const { user } = useAuth();
  const [conv, setConv] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [showRating, setShowRating] = useState(false);
  const [stars, setStars] = useState(5);
  const [review, setReview] = useState("");
  const scrollRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    api.getConversation(id).then(setConv).catch(() => { toast.error("Conversation not found"); nav("/"); });
    const load = () => api.listMessages(id).then(setMsgs).catch(()=>{});
    load();
    const i = setInterval(load, 2500);
    return () => clearInterval(i);
  }, [id]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { await api.sendMessage({ conversation_id: id, content: text }); setText(""); }
    catch { toast.error("Failed to send"); }
  };

  const endChat = async () => {
    await api.endCall(id); // marks completed
    setShowRating(true);
  };

  const submitRating = async () => {
    try { await api.submitRating({ conversation_id: id, stars, review_text: review }); toast.success("Thanks for rating!"); nav("/dashboard/student"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!conv) return <div className="min-h-screen bg-[#0F172A] text-white flex items-center justify-center">Loading...</div>;
  const other = user?.id === conv.student_id ? conv.ambassador_name : conv.student_name;

  return (
    <div className="min-h-screen bg-[#0F172A] text-white flex flex-col">
      <Navbar/>
      <div className="pt-20 flex-1 flex flex-col max-w-3xl mx-auto w-full px-4 pb-4">
        <div className="glass rounded-2xl px-5 py-3 flex items-center justify-between mt-2">
          <div className="flex items-center gap-3">
            <button onClick={()=>nav(-1)} className="text-slate-400 hover:text-white"><ArrowLeft size={20}/></button>
            <div>
              <div className="font-semibold">{other}</div>
              <div className="text-xs text-slate-400">{conv.ambassador_profile?.course} · {conv.ambassador_profile?.year} Year</div>
            </div>
          </div>
          {user?.role === "student" && conv.status !== "completed" && <button data-testid="end-chat" onClick={endChat} className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10">End Chat</button>}
        </div>

        <div ref={scrollRef} className="flex-1 overflow-auto py-4 space-y-2 mt-3">
          {msgs.length === 0 && <div className="text-center text-slate-500 text-sm py-12">No messages yet. Say hi!</div>}
          {msgs.map(m => {
            const mine = m.sender_id === user?.id;
            return (
              <motion.div key={m.id} initial={{opacity:0, x: mine?20:-20}} animate={{opacity:1,x:0}} className={`flex ${mine?"justify-end":"justify-start"}`}>
                <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl ${mine?"bg-[#2563EB] text-white rounded-br-sm":"bg-white/10 border border-white/10 rounded-bl-sm"}`}>
                  <div>{m.content}</div>
                  <div className="text-[10px] opacity-60 mt-1">{new Date(m.created_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}</div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {conv.status !== "completed" ? (
          <form onSubmit={send} className="glass rounded-2xl p-2 flex items-center gap-2">
            <input data-testid="chat-input" value={text} onChange={e=>setText(e.target.value)} placeholder="Type a message..." className="flex-1 bg-transparent px-3 py-2 outline-none"/>
            <button data-testid="chat-send" type="submit" disabled={!text.trim()} className="w-10 h-10 rounded-full bg-[#2563EB] hover:bg-blue-600 flex items-center justify-center disabled:opacity-50"><Send size={16}/></button>
          </form>
        ) : (
          <div className="text-center text-sm text-slate-400 py-3">This conversation has ended.</div>
        )}
      </div>

      {showRating && user?.role === "student" && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="glass rounded-2xl p-6 max-w-md w-full">
            <h3 className="font-display text-xl font-bold mb-3">How was your conversation?</h3>
            <div className="flex justify-center gap-2 mb-4">{[1,2,3,4,5].map(n=><button key={n} data-testid={`star-${n}`} onClick={()=>setStars(n)}><Star size={32} className={n<=stars?"fill-amber-400 text-amber-400":"text-slate-600"}/></button>)}</div>
            <textarea value={review} onChange={e=>setReview(e.target.value)} placeholder="Optional review..." className="w-full bg-[#1E293B] border border-white/10 rounded-lg p-3 mb-3"/>
            <button data-testid="submit-rating" onClick={submitRating} className="w-full py-3 rounded-full bg-[#F59E0B] text-[#0F172A] font-bold">Submit Rating</button>
            <button onClick={()=>nav("/dashboard/student")} className="w-full mt-2 text-sm text-slate-400">Skip</button>
          </div>
        </div>
      )}
    </div>
  );
}
